import React, { useEffect, useRef, forwardRef, useState } from 'react';
import { Pattern, Point, Stitch } from '../../types/pattern';
import { drawAlignmentGuides, drawSymmetryAxes, alignStitch } from '../../utils/alignmentUtils';
import { rotatePoint } from '../../utils/symmetryUtils';
import { StitchPreviewCanvas } from './StitchPreviewCanvas';
import { drawStitch } from './drawStitch';
import { drawOrientationCenter } from './drawOrientationCenter';
import { setOnSvgReady } from '../../stitches/svg-renderer';
import { useCtrlKey } from '../../hooks/useCtrlKey';
import { useShiftKey } from '../../hooks/useShiftKey';
import { getCanvasPoint, isPointNearPoint } from '../../utils/canvasUtils';
import { generateId } from '../../utils/generateId';
import { useToolContext } from '../../contexts/ToolContext';

interface PatternCanvasProps {
  pattern: Pattern;
  onStitchMove: (stitchId: string, updatedStitch: Stitch) => void;
  onStitchDragEnd: () => void;
  onAddStitch: (stitch: Stitch) => void;
  onStitchClick: (stitch: Stitch | null) => void;
  isDraggingOrientationCenter: boolean;
  onOrientationCenterMove: (position: Point) => void;
  onOrientationCenterDoubleClick: () => void;
  setIsDraggingOrientationCenter: (isDragging: boolean) => void;
  onEraserDelete: (stitch: Stitch) => void;
  onRecolorStitch: (stitch: Stitch) => void;
  onToolBrushEnd: () => void;
}

export const PatternCanvas = forwardRef<HTMLCanvasElement, PatternCanvasProps>(({
  pattern,
  onStitchMove,
  onStitchDragEnd,
  onAddStitch,
  onStitchClick,
  isDraggingOrientationCenter,
  onOrientationCenterMove,
  onOrientationCenterDoubleClick,
  setIsDraggingOrientationCenter,
  onEraserDelete,
  onRecolorStitch,
  onToolBrushEnd
}: PatternCanvasProps, ref) => {
  const { tool: { stitchType: selectedStitchType, color: selectedColor, selectedStitch, activeTool } } = useToolContext();
  const localCanvasRef = useRef<HTMLCanvasElement>(null);
  const canvasRef = localCanvasRef;

  // Sync forwarded ref with local ref
  useEffect(() => {
    if (!ref) return;
    if (typeof ref === 'function') {
      ref(localCanvasRef.current);
    } else {
      (ref as React.MutableRefObject<HTMLCanvasElement | null>).current = localCanvasRef.current;
    }
  });
  const isDragging = useRef(false);
  const dragStitchId = useRef<string | null>(null);
  const isToolBrushing = useRef(false);
  const [cursorPosition, setCursorPosition] = useState<Point | null>(null); // CSS pixels relative to canvas
  const [cursorCanvasPosition, setCursorCanvasPosition] = useState<Point | null>(null); // canvas coords (0-800)
  const rafId = useRef(0);
  const isCtrlPressed = useCtrlKey();
  const shiftKey = useShiftKey();
  const isShiftPressed = shiftKey.state;
  const [svgLoadTick, setSvgLoadTick] = useState(0);

  // Trigger canvas repaint when SVG stitch images finish loading
  useEffect(() => {
    setOnSvgReady(() => setSvgLoadTick(t => t + 1));
    return () => setOnSvgReady(() => {});
  }, []);

  useEffect(() => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);

    drawAlignmentGuides(ctx, pattern.center, pattern.radius, pattern.alignmentSettings);

    if (pattern.alignmentSettings.showGuides) {
      drawSymmetryAxes(
        ctx, pattern.center,
        pattern.alignmentSettings.circleStep * pattern.alignmentSettings.circlesCount,
        pattern.symmetryAxes
      );
    }

    pattern.stitches.forEach((stitch: Stitch) => {
      ctx.save();
      ctx.translate(stitch.position.x, stitch.position.y);
      ctx.rotate(stitch.angle);
      drawStitch({ ctx, stitch, isSelected: stitch.id === selectedStitch?.id, stitchScale: pattern.alignmentSettings.stitchScale });
      ctx.restore();
    });

    drawOrientationCenter({ ctx, position: pattern.orientationCenter, settings: pattern.alignmentSettings });
  }, [pattern, canvasRef, selectedStitch, svgLoadTick]);

  const getStitchAtPoint = (point: Point): Stitch | null => {
    for (const stitch of pattern.stitches) {
      if (isPointNearPoint(point, stitch.position)) {
        return stitch;
      }
    }
    return null;
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return;
    const point = getCanvasPoint(event, canvasRef.current);

    if (event.ctrlKey || event.metaKey) {
      onOrientationCenterMove(point);
      return;
    }

    if (activeTool === 'eraser') {
      isToolBrushing.current = true;
      const hit = getStitchAtPoint(point);
      if (hit) onEraserDelete(hit);
      return;
    }
    if (activeTool === 'recolor') {
      isToolBrushing.current = true;
      const hit = getStitchAtPoint(point);
      if (hit) onRecolorStitch(hit);
      return;
    }

    if (selectedStitchType) {
      handleAddStitch(point, selectedColor);
      return;
    }

    if (isPointNearPoint(point, pattern.orientationCenter)) {
      setIsDraggingOrientationCenter(true);
      return;
    }

    const hit = getStitchAtPoint(point);
    if (hit) {
      isDragging.current = true;
      dragStitchId.current = hit.id;
      setCursorPosition(null);
      setCursorCanvasPosition(null);
      onStitchClick(hit);
    } else {
      onStitchClick(null);
    }
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return;
    const point = getCanvasPoint(event, canvasRef.current);

    // Always update cursor position for tool previews
    if (isToolBrushing.current || activeTool || (!isDragging.current && !isDraggingOrientationCenter)) {
      cancelAnimationFrame(rafId.current);
      const rect = canvasRef.current.getBoundingClientRect();
      const cssPoint = { x: event.clientX - rect.left, y: event.clientY - rect.top };
      rafId.current = requestAnimationFrame(() => {
        setCursorPosition(cssPoint);
        setCursorCanvasPosition(point);
      });
    }

    if (isToolBrushing.current && activeTool) {
      const hit = getStitchAtPoint(point);
      if (hit) {
        if (activeTool === 'eraser') onEraserDelete(hit);
        else if (activeTool === 'recolor') onRecolorStitch(hit);
      }
      return;
    } else if (isDraggingOrientationCenter) {
      onOrientationCenterMove(point);
    } else if (isDragging.current && dragStitchId.current) {
      const currentStitch = pattern.stitches.find(s => s.id === dragStitchId.current);
      if (currentStitch) {
        const alignedStitch = alignStitch(
          { ...currentStitch, position: point },
          pattern.orientationCenter
        );
        onStitchMove(currentStitch.id, alignedStitch);
      }
    }
  };

  const handleMouseUp = () => {
    if (isDragging.current && dragStitchId.current) onStitchDragEnd();
    if (isToolBrushing.current) onToolBrushEnd();
    isDragging.current = false;
    dragStitchId.current = null;
    isToolBrushing.current = false;
    setIsDraggingOrientationCenter(false);
  };

  const handleMouseLeave = () => {
    handleMouseUp();
    setCursorPosition(null);
    setCursorCanvasPosition(null);
  };

  const handleDoubleClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return;
    const point = getCanvasPoint(event, canvasRef.current);
    if (isPointNearPoint(point, pattern.orientationCenter)) {
      onOrientationCenterDoubleClick();
    }
  };

  const handleAddStitch = (position: Point, color: string) => {
    if (!selectedStitchType) return;
    const dx = pattern.orientationCenter.x - position.x;
    const dy = pattern.orientationCenter.y - position.y;
    const angle = Math.atan2(dy, dx) - Math.PI / 2;
    onAddStitch({
      id: generateId(),
      type: selectedStitchType!,
      position, angle, orientationAngle: angle, color
    });
  };

  const showStitchPreview = cursorPosition && cursorCanvasPosition && selectedStitchType && !isCtrlPressed && !activeTool;

  // Scale factor: CSS pixels per canvas pixel (for positioning overlays)
  const getCssScale = () => {
    if (!canvasRef.current) return 1;
    const rect = canvasRef.current.getBoundingClientRect();
    return rect.width / 800;
  };

  // Convert canvas coords to CSS coords for overlay positioning
  const toCSS = (p: Point): Point => {
    const s = getCssScale();
    return { x: p.x * s, y: p.y * s };
  };

  return (
    <div style={{ position: 'relative', width: 800, height: 800, flexShrink: 0 }}>
      <canvas
        ref={localCanvasRef}
        width={800}
        height={800}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onDoubleClick={handleDoubleClick}
        style={{ border: '1px solid #ccc', cursor: 'none' }}
      />
      {cursorPosition && isCtrlPressed && (
        <div style={{
          position: 'absolute',
          left: Math.round(cursorPosition.x - 8), top: Math.round(cursorPosition.y - 8),
          width: 16, height: 16, pointerEvents: 'none', zIndex: 1000,
          color: '#666', fontSize: 16, lineHeight: '16px', textAlign: 'center',
        }}>+</div>
      )}
      {cursorPosition && !isCtrlPressed && !activeTool && !selectedStitchType && (
        <div style={{
          position: 'absolute',
          left: Math.round(cursorPosition.x - 3), top: Math.round(cursorPosition.y - 3),
          width: 6, height: 6, borderRadius: '50%',
          backgroundColor: '#999', pointerEvents: 'none', zIndex: 1000,
        }} />
      )}
      {cursorPosition && activeTool === 'eraser' && (
        <div style={{
          position: 'absolute',
          left: Math.round(cursorPosition.x - 8), top: Math.round(cursorPosition.y - 8),
          width: 16, height: 16, pointerEvents: 'none', zIndex: 1000,
          color: '#d32f2f', fontSize: 16, fontWeight: 'bold', lineHeight: '16px', textAlign: 'center',
        }}>✕</div>
      )}
      {cursorPosition && activeTool === 'recolor' && (
        <div style={{
          position: 'absolute',
          left: Math.round(cursorPosition.x - 6), top: Math.round(cursorPosition.y - 6),
          width: 12, height: 12, borderRadius: '50%',
          backgroundColor: selectedColor, border: '2px solid #fff',
          boxShadow: '0 0 2px rgba(0,0,0,0.5)', pointerEvents: 'none', zIndex: 1000,
        }} />
      )}
      {showStitchPreview && (
        <>
          <StitchPreviewCanvas
            position={cursorPosition}
            type={selectedStitchType}
            color={selectedColor}
            center={toCSS(pattern.orientationCenter)}
          />
          {pattern.symmetryAxes >= 2 && !isShiftPressed &&
            Array.from({ length: Math.max(0, Math.min(pattern.symmetryAxes, 24) - 1) }, (_, i) => {
              const angle = (2 * Math.PI * (i + 1)) / pattern.symmetryAxes;
              const rotated = toCSS(rotatePoint(cursorCanvasPosition!, pattern.center, angle));
              const rotatedOC = toCSS(rotatePoint(pattern.orientationCenter, pattern.center, angle));
              return (
                <StitchPreviewCanvas
                  key={i} position={rotated} type={selectedStitchType}
                  color={selectedColor} center={rotatedOC} dimmed
                />
              );
            })
          }
        </>
      )}
    </div>
  );
});
